import { useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { doc, getDoc, onSnapshot, setDoc } from 'firebase/firestore';
import { snapshotHandler } from './util/snapshot';
import { useInvalidateCommunicationsCache, useInvalidateContactListCache, useInvalidateContactRequestsCache, useInvalidateDocumentsSharedFromNotaryListCache, useInvalidateDocumentsSharedToYouListCache, useInvalidateDocumentsVaultListCache, useInvalidateLastNotificationsCache, useInvalidateNotificationsCache, useInvalidateSharedItemsCache, useInvalidateVaultInfoCache, useModules, } from '../';
export const RealtimeCacheSync = ({ firestore, user, onEvent }) => {
    const invalidateCommunicationsCache = useInvalidateCommunicationsCache();
    const invalidateNotificationsCache = useInvalidateNotificationsCache();
    const invalidateLastNotificationsCache = useInvalidateLastNotificationsCache();
    const invalidateContactsCache = useInvalidateContactListCache();
    const invalidateContactRequestsCache = useInvalidateContactRequestsCache();
    const invalidateVaultInfoCache = useInvalidateVaultInfoCache();
    const invalidateDocumentsVaultListCache = useInvalidateDocumentsVaultListCache();
    const invalidateDocumentsSharedFromNotaryListCache = useInvalidateDocumentsSharedFromNotaryListCache();
    const invalidateDocumentsSharedToYouListCache = useInvalidateDocumentsSharedToYouListCache();
    const invalidateSharedItemsCache = useInvalidateSharedItemsCache();
    const { user: { useFrontendSyncConfiguration }, } = useModules();
    const { frontendSyncConfiguration } = useFrontendSyncConfiguration({
        enabled: Boolean(user?.id) && !user?.deactivationRequested,
    });
    const cacheInvalidationFns = useMemo(() => ({
        invalidateNotificationsCache,
        invalidateLastNotificationsCache,
        invalidateContactsCache,
        invalidateContactRequestsCache,
        invalidateVaultInfoCache,
        invalidateDocumentsVaultListCache,
        invalidateDocumentsSharedFromNotaryListCache,
        invalidateDocumentsSharedToYouListCache,
        invalidateSharedItemsCache,
        invalidateCommunicationsCache,
    }), [
        invalidateNotificationsCache,
        invalidateLastNotificationsCache,
        invalidateContactsCache,
        invalidateContactRequestsCache,
        invalidateVaultInfoCache,
        invalidateDocumentsVaultListCache,
        invalidateDocumentsSharedFromNotaryListCache,
        invalidateDocumentsSharedToYouListCache,
        invalidateSharedItemsCache,
        invalidateCommunicationsCache,
    ]);
    useEffect(() => {
        const token = frontendSyncConfiguration?.token;
        const userId = user?.id;
        if (token && userId) {
            const unSub = (async function () {
                try {
                    const auth = getAuth();
                    await signInWithCustomToken(auth, token);
                    const docRef = doc(firestore, `users/${userId}`);
                    const docSnapshot = await getDoc(docRef);
                    if (!docSnapshot.exists()) {
                        await setDoc(docRef, {});
                    }
                    return onSnapshot(docRef, data => snapshotHandler({ data, cacheInvalidationFns, onEvent }));
                }
                catch (error) {
                    Sentry.withScope((scope) => {
                        scope.setTags({
                            description: 'Failed to setup firestore realtime cache sync',
                        });
                        Sentry.captureException(error);
                    });
                }
                return null;
            }());
            return () => {
                unSub.then((fn) => {
                    fn?.();
                });
            };
        }
        return undefined;
    }, [user, firestore, cacheInvalidationFns, frontendSyncConfiguration, onEvent]);
    return null;
};
